<template>
  <main>
    <md-card>
      <md-card-header>
        <div class="md-title">システム管理者</div>
      </md-card-header>

      <loading-spinner v-if="loadAdmins"></loading-spinner>
      <md-card-content v-else-if="errorMessages.length">
        <div class="md-layout">
          <div class="md-layout-item md-size-100">
            <flash-message :errors="errorMessages"></flash-message>
          </div>
        </div>
      </md-card-content>
      <md-card-content v-if="!loadAdmins">
        <md-table v-if="pagination.total !== null">
          <md-table-row>
            <md-table-head v-for="(column, key) in columns" v-bind:key="'column-key-' + key">
              <a href="#" @click="sortBy(infoAdmins, key)"
                 v-bind:class="{'active' : sortKey === key}">{{column.name}}</a>
              <md-icon v-if="sortKey === key && statusSort === 'desc'">arrow_downward</md-icon>
              <md-icon v-else-if="sortKey === key && statusSort === 'asc'">arrow_upward</md-icon>
            </md-table-head>
            <md-table-head>
              <router-link :to="{name:'BackendAdminNew'}">
                <md-button :disabled="sending" class="md-primary md-raised">
                  <md-icon>add</md-icon>
                  <span>新規登録</span>
                </md-button>
              </router-link>
            </md-table-head>
          </md-table-row>
          <md-table-row v-if="infoAdmins.length === 0">
            <md-table-cell colspan="3">{{noDataMessage}}</md-table-cell>
          </md-table-row>
          <md-table-row v-else v-for="(infoAdmin, index) in infoAdmins" v-bind:key="'admin-info-key-' + index">
            <md-table-cell>{{infoAdmin.name}}</md-table-cell>
            <md-table-cell>{{infoAdmin.username}}</md-table-cell>
            <md-table-cell>
              <div class="flexbox">
                <div>
                  <router-link :to="{name: 'BackendAdminNew', params: {id: infoAdmin.id.toString()}}">
                    <md-button :disabled="sending" class="md-raised">
                      <md-icon>edit</md-icon>
                      <span>編集</span>
                    </md-button>
                  </router-link>
                </div>
                <div>
                  <md-button :disabled="sending" class="md-accent md-raised" @click="onDeleteConfirm(infoAdmin)">
                    <md-icon>delete</md-icon>
                    <span>削除</span>
                  </md-button>
                </div>
              </div>
            </md-table-cell>
          </md-table-row>
        </md-table>
        <pagination v-if="pagination.total"
                    :total="pagination.total"
                    :per-page="pagination.per_page"
                    :current-page="pagination.current_page"
                    @pagechanged="onPageChanged"
        />
        <delete-modal
          :md-active.sync="showDeleteModal"
          md-title="システム管理者削除の確認"
          :md-content="'以下のシステム管理者を削除します。<br><span>ログイン: ' + deleteUsername + '</span><br>よろしいですか？'"
          confirm-button-class="md-accent md-raised"
          cancel-button-class="md-raised"
          @md-cancel="onDeleteCancel"
          @md-confirm="onDeleteSubmit"/>
        <md-snackbar :md-active.sync="showSnackBar"
                     :md-duration="snackbarDuration"
                     @md-closed="showSnackBar = false">
          <span>{{ successMessage }}</span>
          <md-button class="md-primary" @click="showSnackBar = false">閉じる</md-button>
        </md-snackbar>
      </md-card-content>
    </md-card>
  </main>
</template>

<script>
import {apiClientWithManagerToken as apiClient, apiList} from '../../../modules/apiClient';
import FlashMessage                                      from '../../../components/FlashMessage';
import Pagination                                        from '../../../components/Pagination';
import Common                                            from "../../../modules/common";
import CommonMixin                                       from "../../../mixins/commonMixin";
import DeleteModal                                       from "../../../components/DeleteModal";
import LoadingSpinner                                    from "../../../components/LoadingSpinner";

export default {
    name      : "AdminList",
    components: {LoadingSpinner, DeleteModal, Pagination, FlashMessage},
    mixins    : [CommonMixin],
    data      : () => ({
      sortKey       : 'name',
      statusSort    : 'asc',
      columns       : {
        name    : {name: '管理者名', sort: 'asc'},
        username: {name: 'ログイン', sort: 'asc'},
      },
      infoAdmins    : [],
      deleteUsername: null,
      deleteInfo    : null,
      // Pagination
      pagination    : {
        total       : null,
        per_page    : Common.limitPageDefault,
        current_page: 1,
      },
      loadAdmins    : false,
    }),
    mounted   : function () {
      this.getAdmins();
      this.getSnackBar();
    },
    methods   : {
      /**
       * Set meta pagination
       * @param meta
       */
      setMetaPagination(meta) {
        this.pagination.total        = meta.total;
        this.pagination.per_page     = meta.per_page;
        this.pagination.current_page = meta.current_page;
      },
      // Clear data
      clearData() {
        this.errorMessages  = [];
        this.successMessage = null;
      },
      /**
       * Set info admins
       */
      getAdmins() {
        this.clearData();
        this.sending    = true;
        this.loadAdmins = true;
        apiClient.get(apiList.manager.list, {
          params: {
            page : this.pagination.current_page,
            limit: this.pagination.per_page,
          }
        })
          .then(response => {
            if (this.hasResponseErrors(response)) {
              return;
            }
            const result = response.data;
            this.setMetaPagination(result.meta);
            this.infoAdmins = result.managers;
          })
          .catch(error => {
            this.errorMessages = [error.message];
          })
          .finally(() => {
            this.sortBy(this.infoAdmins);
            this.sending    = false;
            this.loadAdmins = false;
          });
      },
      /**
       * Call delete admin account api
       */
      deleteAdmin() {
        this.sending = true;
        apiClient.delete(apiList.manager.delete.replace(':manager_id', this.deleteInfo.id))
          .then(response => {
            if (this.hasResponseErrors(response)) {
              return;
            }
            if (Math.ceil((this.pagination.total - 1) / this.pagination.per_page) < this.pagination.current_page) {
              this.pagination.current_page = (this.pagination.current_page - 1) || 1;
            }
            this.getAdmins();
            this.successMessage = Common.messages.manager.deleteSuccess;
            this.showSnackBar   = true;
          })
          .catch(error => {
            this.errorMessages = [error.message];
          })
          .finally(() => {
            this.deleteInfo     = null;
            this.deleteUsername = null;
            this.sending        = false;
          });
      },
      /**
       * Open delete confirm
       */
      onDeleteConfirm(adminInfo) {
        this.deleteUsername  = adminInfo.username;
        this.deleteInfo      = adminInfo;
        this.showDeleteModal = true;
      },
      /**
       * Cancel delete popup
       */
      onDeleteCancel() {
        this.deleteUsername  = null;
        this.deleteInfo      = null;
        this.showDeleteModal = false;
      },
      /**
       * Delete admin account
       */
      onDeleteSubmit() {
        this.showDeleteModal = false;
        this.deleteAdmin();
      },
      /**
       * Change page table
       * @param page
       */
      onPageChanged(page) {
        this.pagination.current_page = page;
        this.getAdmins();
      },
    },
  }
</script>

<style lang="scss" scoped>
  .md-theme-default a:not(.md-button) {
    color: black;
  }

  a.active {
    font-weight: bold;
    color: black;
  }
</style>
